import JSZip from 'jszip';
import { downloadBlob, openBlobInNewTab } from './eventAttachmentDelivery';
import { DEFAULT_PDF_PACK_KEY } from './applicationFormPdfPacks';

/**
 * List PDF packs for a submission; fall back to a single base pack.
 * @param {() => Promise<any>} listPacks
 * @param {string} [fallbackApplicationNumber]
 */
export async function resolveSubmissionPdfPackList(listPacks, fallbackApplicationNumber = 'application') {
  let packs = [];
  try {
    const listRes = await listPacks();
    if (Array.isArray(listRes?.data?.data) && listRes.data.data.length) {
      packs = listRes.data.data;
    }
  } catch (_) {
    /* fall through */
  }
  if (!packs.length) {
    packs = [{
      key: DEFAULT_PDF_PACK_KEY,
      title: 'Application form',
      filename: `${fallbackApplicationNumber || 'application'}.pdf`,
      isDefault: true,
    }];
  }
  return packs;
}

function packFileName(pack, applicationNumber = 'application') {
  if (pack?.filename) return String(pack.filename);
  const key = String(pack?.key || DEFAULT_PDF_PACK_KEY);
  const base = String(applicationNumber || 'application').replace(/[/\\?%*:|"<>]/g, '-');
  if (key === DEFAULT_PDF_PACK_KEY || pack?.isDefault) return `${base}.pdf`;
  return `${base}_${key.replace(/[^A-Za-z0-9_-]+/g, '-')}.pdf`;
}

function filterPacks(packs, dirtyKeys) {
  const keysFilter = Array.isArray(dirtyKeys) && dirtyKeys.length
    ? new Set(dirtyKeys.map((k) => String(k)))
    : null;
  if (!keysFilter) return packs;
  const filtered = packs.filter((p) => keysFilter.has(String(p.key)));
  return filtered.length ? filtered : packs.slice(0, 1);
}

/**
 * Download one or more submission PDF packs.
 * Multiple packs are zipped into a single file so browsers do not block
 * successive automatic downloads (which previously left only the main PDF).
 *
 * @param {object} opts
 * @param {() => Promise<any>} opts.listPacks
 * @param {(packKey: string) => Promise<{ data: Blob|ArrayBuffer }>} opts.downloadPack
 * @param {string} [opts.applicationNumber]
 * @param {'download'|'open'} [opts.mode] open only applies when a single pack is returned
 * @param {string[]|null} [opts.dirtyKeys]
 * @returns {Promise<{ packCount: number; titles: string[]; zipped: boolean }>}
 */
export async function downloadSubmissionPdfPacks({
  listPacks,
  downloadPack,
  applicationNumber = 'application',
  mode = 'download',
  dirtyKeys = null,
} = {}) {
  const allPacks = await resolveSubmissionPdfPackList(listPacks, applicationNumber);
  const packs = filterPacks(allPacks, dirtyKeys);
  const fetched = [];

  for (const pack of packs) {
    const packKey = String(pack.key || DEFAULT_PDF_PACK_KEY);
    const res = await downloadPack(packKey);
    if (!res?.data) {
      throw new Error(`Failed to download PDF pack: ${pack.title || packKey}`);
    }
    fetched.push({
      pack,
      packKey,
      blob: res.data,
      fileName: packFileName(pack, applicationNumber),
      title: String(pack.title || packKey),
    });
  }

  if (!fetched.length) {
    throw new Error('No PDF packs available to download');
  }

  if (fetched.length === 1) {
    const only = fetched[0];
    if (mode === 'open') {
      openBlobInNewTab(only.blob, 'application/pdf');
    } else {
      downloadBlob(only.blob, only.fileName, 'application/pdf');
    }
    return { packCount: 1, titles: [only.title], zipped: false };
  }

  const zip = new JSZip();
  const usedNames = new Set();
  for (const item of fetched) {
    let name = item.fileName || `${item.packKey}.pdf`;
    if (usedNames.has(name)) {
      name = `${item.packKey}_${name}`;
    }
    usedNames.add(name);
    zip.file(name, item.blob);
  }

  const zipBlob = await zip.generateAsync({ type: 'blob' });
  const safeApp = String(applicationNumber || 'application').replace(/[/\\?%*:|"<>]/g, '-');
  downloadBlob(zipBlob, `${safeApp}_pdf-packs.zip`, 'application/zip');

  return {
    packCount: fetched.length,
    titles: fetched.map((f) => f.title),
    zipped: true,
  };
}
